import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import SecureLS from 'secure-ls'
import {
  login,
  loginSalesForce,
  logout,
  signupSalesForce,
  verifyToken,
  verifyUser,
} from 'src/api/userAPI'
import { getAvatar } from 'src/api/guidelinesAPI'
import logoDefault from 'src/assets/icons/sm-scius.svg'

const secureLs = new SecureLS()

const defaultState = {
  sidebarShow: true,
  isLoggedIn: false,
  id: undefined,
  username: undefined,
  email: undefined,
  image: undefined,
  cpf: undefined,
  password: undefined,
  token: undefined,
  completed: undefined,
  role: undefined,
  company: undefined,
  isTokenVerified: false,
  avatar: null,
}

const getStateFromStorage = () => {
  const appcAuth = secureLs.get('appc-auth-mkplace')

  let stateInLocalStorage = {
    sidebarShow: true,
    isLoggedIn: false,
    username: undefined,
    email: undefined,
    image: undefined,
    cpf: undefined,
    password: undefined,
    completed: undefined,
    role: undefined,
    company: undefined,
    isTokenVerified: false,
    avatar: null,
  }

  if (appcAuth) {
    return {
      ...stateInLocalStorage,
      ...appcAuth,
      avatar: appcAuth.avatar ?? null,
    }
  }
  return stateInLocalStorage
}

export const fetchAvatar = createAsyncThunk('user/fetchAvatar', async (_, { rejectWithValue }) => {
  try {
    const response = await getAvatar()
    return response.data
  } catch (error) {
    console.error('Erro ao carregar avatar:', error)
    return rejectWithValue(error.response?.data || 'Erro ao carregar avatar')
  }
})

// 🔹 Novo thunk para verificar usuário via cookie/JWT
export const verifyUserHandler = createAsyncThunk(
  'user/verifyUser',
  async (_, { rejectWithValue }) => {
    try {
      console.log(['AUTH SAML Verifying if user is authenticated'])
      const response = await verifyUser()

      let authState = {}
      authState = {
        sidebarShow: true,
        id: response.data.id,
        username: response.data.username,
        firstname: response.data.firstname,
        lastname: response.data.lastname,
        image: response.data.image,
        token: response.data.token,
        phone: response.data.phone,
        cpf: response.data.cpf,
        email: response.data.email,
        admin: response.data.admin,
        completed: response.data.completed,
        role: response.data.role,
        company: response.data.company,
        companyLogo: response.data.companyLogo,
        jwtToken: response.data.jwt,
        permissions: response.data.permissions,
      }

      return authState
    } catch (error) {
      console.error('Erro ao verificar usuário:', error)
      return rejectWithValue(error.response?.data || 'Erro ao verificar usuário')
    }
  },
)

export const loginHandler = createAsyncThunk('/user/login', async (credentials) => {
  const response = await login(credentials)
  const authState = {
    sidebarShow: true,
    id: response.data.id,
    username: response.data.username,
    firstname: response.data.firstname,
    lastname: response.data.lastname,
    image: response.data.image,
    token: response.data.token,
    phone: response.data.phone,
    cpf: response.data.cpf,
    email: response.data.email,
    admin: response.data.admin,
    completed: response.data.completed,
    role: response.data.role,
    company: response.data.company,
    companyLogo: response.data.companyLogo,
    jwtToken: response.data.jwt,
    permissions: response.data.permissions,
    ...credentials,
  }
  return authState
})

export const loginWithSalesforceToken = createAsyncThunk(
  '/user/login/salesforce',
  async (access_token) => {
    const response = await loginSalesForce(access_token)
    let authState = {}

    if (response.data?.signup) {
      authState = {
        signup: true,
        newUser: response.data.user,
      }
    } else {
      authState = {
        sidebarShow: true,
        id: response.data.id,
        username: response.data.username,
        firstname: response.data.firstname,
        lastname: response.data.lastname,
        image: response.data.image,
        token: response.data.token,
        phone: response.data.phone,
        cpf: response.data.cpf,
        email: response.data.email,
        admin: response.data.admin,
        completed: response.data.completed,
        role: response.data.role,
        company: response.data.company,
        companyLogo: response.data.companyLogo,
        jwtToken: response.data.jwt,
        permissions: response.data.permissions,
        termsVersion: response?.data?.versionAgreedTerms,
        latestTermsVersion: response?.data?.latestTermsVersion,
        ...access_token,
      }
    }

    return authState
  },
)

export const signupWithSalesforceToken = createAsyncThunk(
  '/user/signup/salesforce',
  async (body) => {
    const response = await signupSalesForce(body)
    const authState = {
      sidebarShow: true,
      id: response.data.id,
      username: response.data.username,
      firstname: response.data.firstname,
      lastname: response.data.lastname,
      image: response.data.image,
      token: response.data.token,
      phone: response.data.phone,
      cpf: response.data.cpf,
      email: response.data.email,
      admin: response.data.admin,
      completed: response.data.completed,
      role: response.data.role,
      company: response.data.company,
      companyLogo: response.data.companyLogo,
      jwtToken: response.data.jwt,
      permissions: response.data.permissions,
      termsVersion: response?.data?.versionAgreedTerms,
      latestTermsVersion: response?.data?.latestTermsVersion,
      isTokenVerified: true,
    }

    return authState
  },
)
export const logoutSuccess = createAsyncThunk('user/logout', async (state) => {
  try {
    await logout()
  } catch (err) {
    console.log('backend not ready with logout')
  }
  return defaultState
})

export const verifyTokenHandler = createAsyncThunk('user/verifyToken', async (_, { getState }) => {
  try {
    const response = await verifyToken()
    const currentState = getState().authReducer.user
    return {
      isValid: true,
      user: response.data,
      company: currentState.company,
      companyLogo: currentState.companyLogo,
      role: currentState.role,
    }
  } catch (error) {
    //limpar o storage
    const secureLs = new SecureLS()
    secureLs.remove('appc-auth-mkplace')
    throw error
  }
})

export const authSlice = createSlice({
  name: 'auth',
  initialState: { user: { ...getStateFromStorage() } },
  reducers: {
    updateSuccess: (
      state,
      { firstname, lastname, city, internalState, gender, birthdate, image },
    ) => {
      state.user = {
        ...state.user,
        firstname,
        lastname,
        city,
        state: internalState,
        gender,
        birthdate,
        image,
        completed: true,
      }
    },
    updateProfilePicture: (state, image) => {
      state.user = {
        ...state.user,
        image: image.payload,
      }
    },
    updateCompanyLogo: (state, image) => {
      console.log(image.payload)
      state.user = {
        ...state.user,
        companyLogo: image.payload.File,
      }
    },
    updateShowSideBar: (state, show) => {
      state.user = {
        ...state.user,
        sidebarShow: true,
      }
    },
    updatePlanSuccess: (state, { plan }) => {
      state.user = {
        ...state.user,
        plan,
      }
    },
    updateCompanySuccess: (state, company) => {
      state.user = {
        ...state.user,
        company: company.payload.id,
        companyLogo: company.payload.image || logoDefault,
      }
    },
    setTokenVerified: (state, action) => {
      state.user.isTokenVerified = action.payload
    },
    updateTermsState: (state, version) => {
      state.user = {
        ...state.user,
        termsVersion: version.payload,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginHandler.fulfilled, (state, action) => {
      state.user = { ...action.payload, isLoggedIn: true, isTokenVerified: true }
    })
    builder.addCase(loginWithSalesforceToken.fulfilled, (state, action) => {
      state.user = { ...action.payload, isLoggedIn: true, isTokenVerified: true }
    })
    builder.addCase(signupWithSalesforceToken.fulfilled, (state, action) => {
      state.user = { ...action.payload, isLoggedIn: true }
    })
    builder.addCase(logoutSuccess.fulfilled, (state, action) => {
      state.user = { ...action.payload }
    })
    builder.addCase(verifyTokenHandler.fulfilled, (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload.user,
        company: action.payload.company || state.user.company,
        companyLogo: action.payload.companyLogo || state.user.companyLogo,
        role: action.payload.role || state.user.role,
        isLoggedIn: true,
        isTokenVerified: true,
      }
    })
    builder
      .addCase(verifyTokenHandler.rejected, (state, action) => {
        state.user = { ...defaultState }
      })
      .addCase(verifyUserHandler.fulfilled, (state, action) => {
        state.user = {
          ...state.user,
          ...action.payload,
          isLoggedIn: true,
          isTokenVerified: true,
        }
      })
      .addCase(verifyUserHandler.rejected, (state) => {
        state.user = { ...defaultState }
      })
      .addCase(fetchAvatar.fulfilled, (state, action) => {
        state.user.avatar = action.payload
      })
  },
})

export const {
  updateSuccess,
  updateShowSideBar,
  updatePlanSuccess,
  updateCompanySuccess,
  updateProfilePicture,
  updateCompanyLogo,
  setTokenVerified,
  updateTermsState,
} = authSlice.actions

export default authSlice.reducer
